import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import type { RootState } from "../../../store/store";
import { FREELANCER_LOGIN_PAGE } from "../../auth/routes";
import { clearFreelancerSession } from "../../../store/freelancerAuthSlice";

import { getMyFutureSlotsApi } from "../freelancerApi";

import DenseForest from "../../../img/dense-forest.jpg";

type MyFutureSlotItem = {
  registrationId: string;
  status: string;
  slotId: string;
  day: string;
  startAt: string;
  store: {
    id: string;
    nomeFantasia: string;
    endereco: string | null;
  };
};

type PaginatedMyFutureSlotsResponse = {
  items: MyFutureSlotItem[];
  page: number;
  limit: number;
  total: number;
};

function formatSlotDateTime(iso: string) {
  const dt = new Date(iso);
  const date = dt.toLocaleDateString("pt-BR");
  const time = dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return { date, time };
}

const FreelancerScheduledStoresPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const token = useSelector((state: RootState) => state.freelancerAuth.accessToken);

  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState<MyFutureSlotItem[]>([]);

  const hasToken = useMemo(() => Boolean(token), [token]);

  const fetchSlots = useCallback(async () => {
    if (!token) return;

    try {
      setLoading(true);
      const data = (await getMyFutureSlotsApi(token, {
        page: 1,
        limit: 100,
      })) as unknown as PaginatedMyFutureSlotsResponse;

      const items = (data.items ?? [])
        .slice()
        .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

      setSlots(items);
    } catch (err: any) {
      toast.error(err?.message ?? "Erro ao listar sua agenda.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!hasToken) {
      toast.error("Você precisa estar logado.");
      navigate(FREELANCER_LOGIN_PAGE(), { replace: true });
      return;
    }
    fetchSlots();
  }, [hasToken, navigate, fetchSlots]);

  const handleLogout = () => {
    dispatch(clearFreelancerSession());
    toast.success("Você saiu.");
    navigate(FREELANCER_LOGIN_PAGE(), { replace: true });
  };

  if (!hasToken) return null;

  return (
    <div className="StoreMenuPage SlotsManagerPage ecosystem">
      <img className="ecosystem__bg-img" src={DenseForest} alt="" aria-hidden="true" />
      <div className="ecosystem__content-bg" aria-hidden="true" />

      <div className="freelancer-container ecosystem__content">
        <header className="ecosystem__header">
          <h1 className="ecosystem__title">
            <span className="ecosystem__title-strong">Minha </span>
            <span className="ecosystem__title-light">Agenda</span>
          </h1>

          <p className="ecosystem__subtitle">Veja seus horários futuros agendados.</p>

          <div className="ecosystem__actions">
            <button className="ecosystem__actionBtn" onClick={fetchSlots} disabled={loading}>
              {loading ? "Atualizando..." : "Atualizar"}
            </button>

            <button className="ecosystem__actionBtn" onClick={() => navigate("/")} disabled={loading}>
              Home
            </button>
          </div>
        </header>

        <div className="freelancer-card-table">
          <div className="Box">
            <div className="SlotsTitleRow">
              <div className="BoxTitle" style={{ margin: 0 }}>
                Horários agendados
              </div>
            </div>

            {loading ? (
              <div className="BoxEmpty">Carregando...</div>
            ) : slots.length === 0 ? (
              <div className="BoxEmpty">Você não tem horários agendados no momento.</div>
            ) : (
              <div className="List">
                {slots.map((s) => {
                  const { date, time } = formatSlotDateTime(s.startAt);

                  return (
                    <div key={s.registrationId} className="ListItem" style={{ cursor: "default" }}>
                      <div className="ListItemTitle">{s.store.nomeFantasia}</div>
                      <div className="ListItemSub">
                        {s.store.endereco ? s.store.endereco : "Endereço não informado"}
                      </div>
                      <div className="ListItemSub" style={{ marginTop: 6 }}>
                        <strong>{date}</strong> - {time}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreelancerScheduledStoresPage;
